<template>
    <div>
        <div class="gradient-box gradient-box-peach" />
        <div :key="id" data-test-id="meeting-edit" class="row">
            <div class="col-md-8">
                <l-card-content>
                    <l-headline :title="title" :tabs="tabs"> </l-headline>

                    <l-confirmation-modal
                        v-if="removeWarning"
                        :text="$t('meetings.labelRemoveWarning')"
                        @confirm="updateMeeting({ scope: 'remove' })"
                        @cancel="toggleRemoveWarning()"
                    />

                    <l-confirmation-modal
                        v-if="archiveWarning"
                        :text="$t('meetings.labelArchiveWarning')"
                        @confirm="archiveMeeting()"
                        @cancel="toggleArchiveWarning()"
                    />

                    <l-meeting-action-items
                        :meeting="meeting"
                        :meeting-mode-enabled="meetingModeEnabled"
                        @update="updateActionItems"
                    />
                </l-card-content>
            </div>
            <div class="col-md-4">
                <l-card-content style="margin-left: -10px">
                    <!-- Participant pictures -->

                    <l-profile-picture
                        v-for="user in meeting.participants"
                        :key="user._id"
                        :style="{ marginRight: '-10px' }"
                        size="s"
                        :user="user"
                    />

                    <!--/ Participant pictures -->

                    <!-- Past Meetings and Search -->

                    <div
                        class="clickable mouseover-parent tw-mt-6"
                        @click="togglePastMeetings()"
                        @keyup.enter="togglePastMeetings()"
                    >
                        <span v-if="showPastMeetings">
                            <span class="agenda-question">{{
                                $t("meetings.labelPastMeetingsHide")
                            }}</span>
                            <i class="fa fa-caret-down color-grey l-default-margin-s mouseover"></i>
                        </span>
                        <span v-else>
                            <span class="agenda-question">{{
                                $t("meetings.labelPastMeetings")
                            }}</span>
                            <i
                                class="fa fa-caret-right color-grey l-default-margin-s mouseover"
                            ></i>
                        </span>
                    </div>

                    <div v-if="showPastMeetings">
                        <div class="hr-small"></div>

                        <l-input-text
                            ref="talkingPoint"
                            v-model="searchKeyword"
                            :label="''"
                            :placeholder="$t(`meetings.placeholderSearch`)"
                            type="text"
                            optional
                            hide-help-block
                            size="m"
                            data-test-id="meeting-search"
                        />

                        <div v-if="searchKeyword !== ''">
                            <!-- Results -->
                            <div
                                v-for="item in searchResults"
                                :key="item._id"
                                class="tw-border tw-rounded-lg tw-p-4 tw-border-gray-200 tw-border-solid tw-mt-4 tw-cursor-pointer"
                            >
                                <a
                                    class="fake-link"
                                    @click="() => getDetails(item._id)"
                                    @keyup.enter="() => getDetails(item._id)"
                                >
                                    <span :class="{ itemSelected: meeting._id === item._id }">
                                        <i class="fal fa-caret-right r-default-margin-s"></i>
                                        {{ item.time | moment("MMM D") }} @
                                        <span>{{ item.time | moment(timeFormat) }}</span>
                                    </span>
                                    <span> - {{ item.matches }}x </span>
                                </a>
                            </div>
                            <!--/Results -->

                            <!-- Searching -->
                            <div v-if="isSearching" class="color-grey">
                                <div class="hr-small"></div>
                                {{ $t("meetings.labelSearching") }}
                            </div>
                            <!--/Searching -->

                            <!-- No Results -->
                            <div
                                v-if="!isSearching && searchResults && searchResults.length === 0"
                                class="color-grey"
                            >
                                <div class="hr-small"></div>
                                {{ $t("meetings.labelNoResults") }}
                            </div>
                            <!--/No Results -->
                        </div>

                        <div v-if="searchKeyword === ''">
                            <div
                                v-for="c in meeting.previousList"
                                :key="c._id"
                                class="tw-border tw-rounded-lg tw-p-3 tw-border-gray-200 tw-border-solid tw-mt-4 tw-cursor-pointer"
                            >
                                <a
                                    class="fake-link"
                                    :class="{ itemSelected: meeting._id === c._id }"
                                    @click="() => getDetails(c._id)"
                                    @keyup.enter="() => getDetails(c._id)"
                                >
                                    <i class="fal fa-caret-right r-default-margin-s"></i>
                                    <span v-if="!c.isPrevious">{{ $t("meetings.tabNext") }}</span>
                                    {{ c.cycle.nextTime | moment("MMM D") }} @
                                    {{ c.cycle.nextTime | moment(timeFormat) }}
                                </a>
                            </div>
                        </div>
                    </div>
                    <!-- Past Meetings and Search -->

                    <!-- Goals -->
                    <div v-if="rights.G_VIEW" class="tw-mt-4">
                        <l-meeting-goal-section
                            :meeting="meeting"
                            :meeting-mode-enabled="meetingModeEnabled"
                        />
                    </div>
                    <!--/Goals -->

                    <!-- Feedback -->
                    <div v-if="rights.F_VIEW && meeting.participants.length === 2" class="tw-mt-4">
                        <l-meeting-feedback-section
                            :meeting="meeting"
                            :meeting-mode-enabled="meetingModeEnabled"
                        />
                    </div>
                    <!--/Feedback -->

                    <!-- Notes -->

                    <l-meeting-notes :meeting="meeting" class="tw-mt-4" />

                    <!--/Notes -->
                </l-card-content>
            </div>

            <l-modal
                v-show="showSettings"
                size="l"
                :close-on-backdrop-click="false"
                @close="closeSettings"
            >
                <template #header>
                    {{ $t(`meetings.titleMeetingSettings`) }}
                </template>
                <template #body>
                    <router-view :close="closeSettings"></router-view>
                </template>
            </l-modal>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LInputText from "@/components/LInputText";
import LMeetingFeedbackSection from "@/components/LMeetingFeedbackSection";
import LMeetingGoalSection from "@/components/LMeetingGoalSection";
import LMeetingNotes from "@/components/LMeetingNotes";
import LMeetingActionItems from "@/components/LMeetingActionItems";
import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LProfilePicture from "@/components/LProfilePicture";
import LModal from "@/components/LModal";
import LConfirmationModal from "@/components/LConfirmationModal";

import icon from "@/img/icons/settings.png";
import UsersTeamsAddUsersVue from "./UsersTeamsAddUsers.vue";

export default {
    components: {
        LInputText,
        LMeetingFeedbackSection,
        LMeetingGoalSection,
        LMeetingNotes,
        LMeetingActionItems,
        LCardContent,
        LHeadline,
        LProfilePicture,
        LModal,
        LConfirmationModal,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            meeting: {
                agendaQuestions: [],
                participants: [],
                otherUsers: [],
                cycle: {
                    nextTime: null,
                },
            },

            meetingModeEnabled: false,
            removeWarning: false,
            archiveWarning: false,
            showPastMeetings: false,
            showSettings: false,

            updating: 0,
            error: "",
            searchKeyword: "",
            isSearching: false,

            refreshIntervalId: null,
            visibilityState: "visible", // https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilityState
        };
    },
    computed: {
        ...mapGetters({
            getMeetingDetails: "meetings/meetings/details",
            user: "users/current/user",
            rights: "users/current/rights",
            teamSettings: "users/current/teamSettings",
            searchResults: "meetings/meetings/getSearchResults",
            createdMeetingId: "meetings/meetings/latestCreatedId",
        }),
        gCalConferenceLink() {
            return (
                (this.meeting.gCalEvent &&
                    this.meeting.gCalEvent.conferenceData &&
                    this.meeting.gCalEvent.conferenceData.uri) ||
                null
            );
        },
        timeFormat() {
            if (this.teamSettings.useMeridianTime) {
                return "hh:mm a";
            }
            return "HH:mm";
        },

        canArchiveMeeting() {
            return (
                (this.meeting._id && this.meeting.status === "sent" && !this.meeting.isPrevious) ||
                (this.meeting._id &&
                    this.meeting.cycle.frequency === "manual" &&
                    this.meeting.status !== "complete" &&
                    !this.meeting.isPrevious)
            );
        },
        title() {
            const title = this.meeting.title || "Meeting";
            const time = this.$moment(this.meeting.cycle?.nextTime).format(
                `MMM D, ${this.timeFormat}`,
            );
            return `${title} @ ${time}`;
        },
        tabs() {
            return [
                {
                    label: this.$t("meetings.btnBack"),
                    show: true,
                    handler: this.goBack,
                    icon: "fal fa-caret-left",
                    iconBeforeLabel: true,
                },
                {
                    label: this.$t("meetings.btnComplete"),
                    show: this.canArchiveMeeting,
                    handler: this.toggleArchiveWarning,
                    active: false,
                    popover: this.$t("meetings.btnArchive"),
                    icon: "fal fa-check",
                    iconBeforeLabel: true,
                },
                {
                    label: this.$t("meetings.labelRemove"),
                    show: this.meeting._id && this.meeting.isPrevious,
                    handler: this.toggleRemoveWarning,
                    active: false,
                    icon: "fal fa-trash",
                    iconBeforeLabel: true,
                },
                {
                    label: this.$t("meetings.btnMeetingMode"),
                    icon: this.meetingModeEnabled ? "fal fa-square" : "fal fa-check-square",
                    iconBeforeLabel: true,
                    show: true,
                    handler: this.toggleMeetingMode,
                    active: false,
                    testId: "meeting-meeting-mode",
                    popover: this.$t("meetings.labelMeetingMode"),
                },
                {
                    tabClass: "space",
                    show: true,
                },

                {
                    label: this.$t("meetings.labelSettings"),
                    show: true,
                    handler: this.displaySettings,
                    testId: "meeting-setting-btn",
                    isButton: UsersTeamsAddUsersVue,
                },
                {
                    label: this.$t("meetings.joinConference"),
                    show: Boolean(this.gCalConferenceLink),
                    handler: this.goToConference,
                    tabClass: "btn-primary tw-ml-4",
                    testId: "meeting-setting-btn",
                    icon: "fal fa-video",
                    iconBeforeLabel: true,
                    isButton: true,
                },
            ];
        },
    },
    watch: {
        id: {
            immediate: true,
            handler(id) {
                this.meeting = this.getMeetingDetails(id);

                if (!this.meeting._id) {
                    this.$router.replace(`/meetings/list`);
                }
            },
        },
        searchKeyword: {
            handler(query) {
                if (!query) {
                    return;
                }

                this.isSearching = true;

                this.$store
                    .dispatch({
                        type: "meetings/meetings/getSearchResults",
                        payload: { query, groupToken: this.meeting.groupToken },
                    })
                    .finally(() => {
                        this.isSearching = false;
                    });
            },
        },

        meeting: {
            immediate: true,
            handler(meeting) {
                meeting.myTalkingPointsBySection = {};
                meeting.othersTalkingPointsBySection = {};

                //  when we delete the meeting, participants are not returned. this will cause issue.
                if (!meeting.participants) {
                    meeting.participants = [];
                }
                if (!meeting.otherUsers) {
                    meeting.otherUsers = [];
                }

                // Update title
                const participants = [this.user, ...meeting.otherUsers].filter(Boolean);
                const title =
                    meeting.title ||
                    participants.reduce(
                        (names, user, i) =>
                            i
                                ? `${names} + ${user.name || user.firstname}`
                                : user.name || user.firstname,
                        "",
                    );

                this.$emit("title", title, participants);
            },
        },
        $route: {
            immediate: true,
            handler(newRoute) {
                if (newRoute?.name === "MeetingsSettings") {
                    this.showSettings = true;
                } else {
                    this.showSettings = false;
                }
            },
        },
    },
    created() {
        this.icon = icon;

        // Poll refreshed meeting details every 5 seconds to simulate real-time updates
        // Note: We might at some point change this implementation to use WebSockets instead of polling
        this.refreshIntervalId = setInterval(this.refreshMeetingDetails, 5000);
    },
    mounted() {
        // Visibility state handlers
        this.visibilityStateHidden = () => {
            this.visibilityState = "hidden";
        };
        this.visibilityStateVisible = () => {
            this.visibilityState = "visible";
        };

        window.addEventListener("blur", this.visibilityStateHidden);
        window.addEventListener("focus", this.visibilityStateVisible);
    },
    beforeDestroy() {
        // Remove visibility state handlers
        window.removeEventListener("blur", this.visibilityStateHidden);
        window.removeEventListener("focus", this.visibilityStateVisible);

        // Clear polling interval
        clearInterval(this.refreshIntervalId);
    },
    methods: {
        getDetails(id) {
            this.$router.push(`/meetings/details/${id}`);
        },
        togglePastMeetings() {
            this.showPastMeetings = !this.showPastMeetings;
        },

        displaySettings() {
            // We always want to display the settings of the latest meeting, as updating settings of a past meeting might cause
            // some issues.
            const latestMeetingId = this.meeting?.previousList?.[0]?._id;

            // We save the current meeting's id so that we can return to this specific meeting when closing the settings modal.
            this.currentId = this.id;

            if (latestMeetingId) {
                this.$router.replace(`/meetings/details/${latestMeetingId}/settings`);
            }
        },
        closeSettings() {
            this.$router.replace(`/meetings/details/${this.currentId || this.id}`);
        },

        toggleMeetingMode() {
            this.meetingModeEnabled = !this.meetingModeEnabled;
        },

        goToConference() {
            window.open(this.gCalConferenceLink, "_blank", "noopener noreferrer");
        },

        archiveMeeting() {
            this.updateMeeting({ scope: "archive" });
            this.toggleArchiveWarning();
        },

        goBack() {
            this.$router.go(-1);
        },

        toggleRemoveWarning() {
            this.removeWarning = !this.removeWarning;
        },

        toggleArchiveWarning() {
            this.archiveWarning = !this.archiveWarning;
        },

        updateMeeting({ scope }) {
            const meeting = this.meeting;

            let data = {};

            if (scope === "deactivate") {
                // only current meeting can be updated

                const cycle = meeting.cycle;

                // Add timezone information
                cycle.tz = Intl?.DateTimeFormat?.().resolvedOptions?.()?.timeZone ?? cycle.tz;

                data = {
                    _id: meeting._id,
                    groupToken: meeting.groupToken,
                    participants: meeting.participants,
                    cycle: cycle,
                    title: meeting.title,
                    copyTalkingPoints: meeting.copyTalkingPoints,
                };
            } else if (scope === "archive") {
                data = {
                    _id: meeting._id,
                    status: "complete",
                };
            } else if (scope === "remove") {
                data = {
                    _id: meeting._id,
                    status: "removed",
                };
            }

            this.$store
                .dispatch({
                    type: "meetings/meetings/update",
                    payload: { data },
                })
                .then(() => {
                    if (scope === "remove") {
                        this.$router.replace("/meetings/");
                        return;
                    } else if (scope === "archive") {
                        // move to the newly created meeting :)
                        this.$router.push(`/meetings/details/${this.createdMeetingId}`);
                        return;
                    }
                });
        },

        updateMeetingDetails() {
            this.meeting = this.getMeetingDetails(this.id);
        },

        updateActionItems(request) {
            this.updating = this.updating + 1;

            this.$store.dispatch({
                type: "meetings/meetings/updateInProgress",
                payload: { updating: true },
            });

            this.updateMeetingDetails();

            request.finally(() => {
                this.updating = Math.max(0, this.updating - 1);

                // Only remove the updateInProgress flag in the store if there isn't another item being updated
                if (!this.updating) {
                    this.$store.dispatch({
                        type: "meetings/meetings/updateInProgress",
                        payload: { updating: false },
                    });
                }
            });
        },

        // Re-fecth meeting details to refresh the content in near real-time
        async refreshMeetingDetails() {
            // Only refresh details if user is not looking at the settings
            if (this.showSettings) {
                return;
            }

            // Only refresh is document is visible
            if (this.visibilityState !== "visible") {
                return;
            }

            // To avoid stale updates, return if currently updating
            if (this.updating) {
                return;
            }

            await this.$store
                .dispatch({
                    type: "meetings/meetings/getDetails",
                    payload: { _id: this.id },
                })
                .catch(() => {
                    // Clear polling interval
                    this.refreshIntervalId = clearInterval(this.refreshIntervalId);
                });

            this.updateMeetingDetails();
        },
    },
};
</script>

<style scoped>
.padding-clear {
    padding: 0;
}
.date-picker {
    margin-top: 0;
}
.meeting-edit >>> .date-picker-wrapper {
    margin-top: 0 !important;
    min-width: 110px;
}
.selected {
    background-color: #f5f5f5;
    color: #4285f4;
}

.meeting-search-results {
    padding-top: 10px;
    padding-bottom: 10px;
    margin-top: 0px;
}
</style>
